/**
 * Resilient engine socket.
 *
 * - Reconnects with jittered exponential backoff and resumes the same
 *   session (speaker: resume token; listener: code + lastSeq).
 * - Tracks the last sequence number seen; a hole triggers a `sync`.
 * - Buffers outgoing audio while offline (up to `maxBufferedMs`) and flushes
 *   it after the engine says `ready`, so a short drop loses no speech.
 * - A heartbeat closes links that stopped delivering anything.
 */

import { parseEvent, seqOf, type ServerEvent } from "./protocol.ts";

export type SocketLike = {
  readyState: number;
  bufferedAmount: number;
  binaryType: string;
  send(data: string | ArrayBuffer): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
};

export type Clock = {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
  now(): number;
};

export type ConnectionState = "connecting" | "open" | "reconnecting" | "closed";

export type ResumeInfo = { sessionId: string; resumeToken: string | null; lastSeq: number };

export type ConnectionOptions = {
  url: string;
  first: () => object;
  resume: (info: ResumeInfo) => object;
  onEvent: (event: ServerEvent) => void;
  /** Binary frames: PCM audio for listeners who asked for speech. */
  onAudio?: (chunk: ArrayBuffer) => void;
  onState?: (state: ConnectionState, detail: { attempt: number; delayMs?: number; reason?: string }) => void;
  makeSocket?: (url: string) => SocketLike;
  clock?: Clock;
  random?: () => number;
  maxAttempts?: number;
  maxBufferedMs?: number;
  frameMs?: number;
  heartbeatMs?: number;
  deadAfterMs?: number;
};

const OPEN = 1;
const HIGH_WATER = 256 * 1024;
// Close codes after which retrying cannot help.
const FINAL_CODES = new Set([1000, 4400, 4401, 4403, 4404, 4429, 4503]);

const realClock: Clock = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
  setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
  clearInterval: (h) => globalThis.clearInterval(h as ReturnType<typeof setInterval>),
  now: () => Date.now(),
};

export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(8000, 300 * 2 ** attempt);
  return Math.round(base * (0.75 + random() * 0.5));
}

export class EngineConnection {
  sessionId: string | null = null;
  resumeToken: string | null = null;
  lastSeq = -1;
  state: ConnectionState = "connecting";

  private ws: SocketLike | null = null;
  private ready = false;
  private attempt = 0;
  private intentional = false;
  private fatal = false;
  private stopping = false;
  private queue: ArrayBuffer[] = [];
  private droppedMs = 0;
  private lastHeard = 0;
  private retryTimer: unknown = null;
  private heartbeat: unknown = null;
  private readonly opts: Required<Omit<ConnectionOptions, "onState" | "makeSocket" | "onAudio">> &
    Pick<ConnectionOptions, "onState" | "makeSocket" | "onAudio">;

  constructor(options: ConnectionOptions) {
    this.opts = {
      clock: realClock,
      random: Math.random,
      maxAttempts: 12,
      maxBufferedMs: 15000,
      frameMs: 80,
      heartbeatMs: 5000,
      deadAfterMs: 15000,
      ...options,
    };
  }

  open(): void {
    this.intentional = false;
    this.connect();
    this.heartbeat = this.opts.clock.setInterval(() => this.beat(), this.opts.heartbeatMs);
  }

  /** Queue or send one binary audio frame. */
  sendAudio(frame: ArrayBuffer): void {
    if (this.intentional || this.fatal || this.stopping) return;
    const ws = this.ws;
    if (this.ready && ws && ws.readyState === OPEN && this.queue.length === 0 && ws.bufferedAmount < HIGH_WATER) {
      ws.send(frame);
      return;
    }
    this.queue.push(frame);
    const limit = Math.max(1, Math.floor(this.opts.maxBufferedMs / this.opts.frameMs));
    while (this.queue.length > limit) {
      this.queue.shift();
      this.droppedMs += this.opts.frameMs;
    }
    if (this.ready) this.flush();
  }

  send(message: object): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return false;
    ws.send(JSON.stringify(message));
    return true;
  }

  /** Ask the engine to finalize. The socket closes after `stopped`. */
  stop(): void {
    this.flush();
    this.stopping = true;
    if (!this.send({ type: "stop" })) this.close();
  }

  close(): void {
    this.intentional = true;
    this.opts.clock.clearTimeout(this.retryTimer);
    this.opts.clock.clearInterval(this.heartbeat);
    this.ws?.close(1000, "client closed");
    this.setState("closed", {});
  }

  get bufferedFrames(): number {
    return this.queue.length;
  }

  // -- internals ------------------------------------------------------------

  private setState(state: ConnectionState, detail: { delayMs?: number; reason?: string }): void {
    this.state = state;
    this.opts.onState?.(state, { attempt: this.attempt, ...detail });
  }

  private connect(): void {
    this.ready = false;
    let ws: SocketLike;
    try {
      ws = this.opts.makeSocket ? this.opts.makeSocket(this.opts.url) : (new WebSocket(this.opts.url) as unknown as SocketLike);
    } catch {
      this.scheduleRetry("could not open socket");
      return;
    }
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.setState(this.attempt === 0 && !this.sessionId ? "connecting" : "reconnecting", {});

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.lastHeard = this.opts.clock.now();
      const message = this.sessionId
        ? this.opts.resume({ sessionId: this.sessionId, resumeToken: this.resumeToken, lastSeq: this.lastSeq })
        : this.opts.first();
      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      this.lastHeard = this.opts.clock.now();
      if (ev.data instanceof ArrayBuffer) {
        this.opts.onAudio?.(ev.data);
        return;
      }
      if (typeof ev.data !== "string") return;
      const event = parseEvent(ev.data);
      if (!event) return;
      this.track(event);
      this.opts.onEvent(event);
    };

    ws.onerror = () => {
      /* onclose follows and decides */
    };

    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ready = false;
      this.ws = null;
      if (this.intentional) return;
      if (this.fatal || FINAL_CODES.has(ev.code) || (this.stopping && !this.sessionId)) {
        this.finish(ev.reason || `closed (${ev.code})`);
        return;
      }
      this.scheduleRetry(ev.reason || `connection lost (${ev.code})`);
    };
  }

  private track(event: ServerEvent): void {
    if (event.type === "ready") {
      if (this.sessionId && event.sessionId !== this.sessionId) this.lastSeq = 0;
      if (!this.sessionId) this.lastSeq = 0;
      this.sessionId = event.sessionId;
      if (event.resumeToken) this.resumeToken = event.resumeToken;
      this.ready = true;
      this.attempt = 0;
      this.setState("open", {});
      this.flush();
      // A stop sent just before a drop may never have arrived; repeat it.
      if (this.stopping) this.send({ type: "stop" });
      return;
    }
    if (event.type === "snapshot") {
      this.lastSeq = event.seq;
      return;
    }
    if (event.type === "error" && event.fatal) this.fatal = true;
    if (event.type === "status" && event.state === "stopped" && this.stopping) {
      this.close();
      return;
    }
    const seq = seqOf(event);
    if (seq === null || event.type === "status") {
      if (seq !== null && seq > this.lastSeq) this.lastSeq = seq;
      return;
    }
    if (this.lastSeq >= 0 && seq > this.lastSeq + 1) {
      // Missed events (should not happen on one socket): resynchronise.
      this.send({ type: "sync" });
    }
    if (seq > this.lastSeq) this.lastSeq = seq;
  }

  private flush(): void {
    const ws = this.ws;
    if (!this.ready || !ws || ws.readyState !== OPEN) return;
    if (this.droppedMs > 0) {
      ws.send(JSON.stringify({ type: "gap", ms: this.droppedMs }));
      this.droppedMs = 0;
    }
    while (this.queue.length && ws.bufferedAmount < HIGH_WATER) {
      ws.send(this.queue.shift()!);
    }
  }

  private scheduleRetry(reason: string): void {
    if (this.attempt >= this.opts.maxAttempts) {
      this.finish(`gave up after ${this.attempt} attempts: ${reason}`);
      return;
    }
    const delayMs = backoffMs(this.attempt, this.opts.random);
    this.attempt += 1;
    this.setState("reconnecting", { delayMs, reason });
    this.retryTimer = this.opts.clock.setTimeout(() => {
      if (!this.intentional) this.connect();
    }, delayMs);
  }

  private finish(reason: string): void {
    this.opts.clock.clearInterval(this.heartbeat);
    this.setState("closed", { reason });
  }

  private beat(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== OPEN) return;
    ws.send(JSON.stringify({ type: "ping", t: this.opts.clock.now() }));
    if (this.ready) this.flush();
    // The engine is silent while it finalizes; do not mistake that for a dead link.
    if (!this.stopping && this.opts.clock.now() - this.lastHeard > this.opts.deadAfterMs) {
      ws.close(4000, "heartbeat timeout");
      // Some browsers delay onclose on dead links; act now.
      ws.onclose?.({ code: 4000, reason: "heartbeat timeout" });
    }
  }
}
